import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { useUI } from "../lib/cart-context";

/**
 * Slide-down mega-menu panel that expands below the header in normal document
 * flow (no overlay, no backdrop). Replaces the old left-side CategoryDrawer
 * overlay. State is still driven by `categoryDrawerOpen` from `useUI()` so
 * existing header buttons keep working.
 */
export default function MegaCategoriesPanel() {
  const { categoryDrawerOpen, setCategoryDrawerOpen, setCartOpen } = useUI();
  const [tree, setTree] = useState([]);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const firstLinkRef = useRef(null);

  // Mutual exclusion — opening the mega-menu closes the cart panel.
  useEffect(() => {
    if (categoryDrawerOpen) setCartOpen(false);
  }, [categoryDrawerOpen, setCartOpen]);

  useEffect(() => {
    let alive = true;
    api
      .get("/categories")
      .then((r) => alive && setTree(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Auto-close on route change
  useEffect(() => {
    setCategoryDrawerOpen(false);
    setMobileExpanded(null);
  }, [location.pathname, location.search, setCategoryDrawerOpen]);

  // Escape closes the panel
  useEffect(() => {
    if (!categoryDrawerOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setCategoryDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [categoryDrawerOpen, setCategoryDrawerOpen]);

  // Focus the first link on open (a11y)
  useEffect(() => {
    if (categoryDrawerOpen && firstLinkRef.current) {
      const id = requestAnimationFrame(() => {
        try {
          firstLinkRef.current.focus({ preventScroll: true });
        } catch {
          /* noop */
        }
      });
      return () => cancelAnimationFrame(id);
    }
  }, [categoryDrawerOpen]);

  const close = () => setCategoryDrawerOpen(false);

  return (
    <div
      id="mega-categorias"
      role="region"
      aria-label="Categorías"
      data-testid="mega-categorias-panel"
      data-open={categoryDrawerOpen ? "true" : "false"}
      aria-hidden={!categoryDrawerOpen}
      inert={!categoryDrawerOpen ? "" : undefined}
      className={`w-full bg-white overflow-hidden transition-[max-height,box-shadow,border-color] duration-300 ease-out ${
        categoryDrawerOpen
          ? "max-h-[70vh] md:max-h-[60vh] shadow-[0_8px_16px_-8px_rgba(0,0,0,0.15)] border-b border-neutral-200"
          : "max-h-0 shadow-none border-b border-transparent"
      }`}
    >
      {/* Desktop / tablet grid */}
      <div className="hidden md:block">
        <div className="mx-auto max-w-7xl px-6 py-8 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-6">
            {tree.map((cat, idx) => (
              <div key={cat.id} data-testid={`mega-col-${cat.slug}`}>
                <Link
                  ref={idx === 0 ? firstLinkRef : undefined}
                  to={`/categoria/${cat.slug}`}
                  onClick={close}
                  data-testid={`mega-cat-${cat.slug}`}
                  className="block font-semibold text-neutral-900 mb-2 hover:text-[#00953B] focus:outline-none focus:text-[#00953B]"
                >
                  {cat.name}
                </Link>
                <ul className="space-y-1.5">
                  {(cat.children || []).map((sub) => (
                    <li key={sub.id}>
                      <Link
                        to={`/categoria/${cat.slug}?sub=${sub.slug}`}
                        onClick={close}
                        data-testid={`mega-subcat-${sub.slug}`}
                        className="text-sm text-neutral-600 hover:text-[#00953B] block"
                      >
                        {sub.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {/* Phase 24 — featured chip row: Sabores de casa */}
          <div className="mt-6 pt-4 border-t border-neutral-100 flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-neutral-400">
              Destacado
            </span>
            <Link
              to="/buscar?is_maghrebi=1"
              onClick={close}
              data-testid="mega-chip-sabores-de-casa"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1FAF3] border border-[#c9ecd4] text-sm font-semibold text-[#00753a] hover:bg-emerald-100 hover:border-[#00953B] transition-colors"
            >
              <span aria-hidden>🇩🇿</span>
              Sabores de casa
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile accordion */}
      <div className="md:hidden">
        <div className="overflow-y-auto max-h-[70vh] px-4 py-2">
          {tree.map((cat, idx) => {
            const expanded = mobileExpanded === cat.id;
            return (
              <div key={cat.id} className="border-b border-neutral-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() =>
                    setMobileExpanded((prev) => (prev === cat.id ? null : cat.id))
                  }
                  aria-expanded={expanded}
                  data-testid={`mega-cat-mobile-${cat.slug}`}
                  className="w-full flex items-center justify-between py-3 text-left font-semibold text-neutral-900"
                >
                  <span>{cat.name}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-neutral-500 transition-transform ${expanded ? "rotate-180" : ""}`}
                  />
                </button>
                {expanded && (
                  <div className="pb-3 pl-3 flex flex-col">
                    <Link
                      ref={idx === 0 ? firstLinkRef : undefined}
                      to={`/categoria/${cat.slug}`}
                      onClick={close}
                      data-testid={`mega-cat-mobile-all-${cat.slug}`}
                      className="py-1.5 text-sm font-medium text-[#00953B]"
                    >
                      Ver todo en {cat.name}
                    </Link>
                    {(cat.children || []).map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/categoria/${cat.slug}?sub=${sub.slug}`}
                        onClick={close}
                        data-testid={`mega-subcat-mobile-${sub.slug}`}
                        className="py-1.5 text-sm text-neutral-600 hover:text-[#00953B]"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
